/**
 * Les mots du dossier, en français et en anglais.
 *
 * Comme le reste de /outils, les libellés vivent avec le code et non dans
 * next-intl : la mise en page publique ne passe aux composants clients qu'une
 * partie des messages (voir le commentaire de lib/tools/registry.js).
 *
 * Ils servent à trois endroits qui doivent dire la même chose : les six
 * questions de « Votre situation », le récapitulatif du dossier, et la
 * proposition « Garder dans votre dossier ? » sous chaque outil.
 */

import { LIBELLES_ECHEANCES } from "./deadlineLabels.js";

export const LIBELLES_CHAMPS = {
    fr: {
        statut: "Statut",
        activite: "Activité",
        artisan: "Inscrit comme artisan",
        dateDebut: "Début d'activité",
        caCetteAnnee: "Chiffre d'affaires de cette année",
        caAnDernier: "Chiffre d'affaires de l'an dernier",
        enCouple: "Déclaration d'impôts",
        enfants: "Enfants à charge",
        autresRevenus: "Autres revenus du foyer, par an",
        objectifMensuel: "Revenu visé, par mois",
        fraisMensuels: "Frais, par mois",
        tarifJournalier: "Tarif journalier",
        optionFiscale: "Impôt sur le revenu",
        acre: "ACRE",
        tvaFacturee: "TVA facturée",
        // Identité
        civilite: "Civilité",
        prenoms: "Prénoms",
        nom: "Nom",
        nomNaissance: "Nom de naissance",
        dateNaissance: "Date de naissance",
        villeNaissance: "Ville de naissance",
        paysNaissance: "Pays de naissance",
        nationalite: "Nationalité",
        voie: "Adresse",
        complement: "Complément d'adresse",
        codePostal: "Code postal",
        commune: "Commune",
        email: "E-mail",
        telephone: "Téléphone",
        siret: "SIRET",
        codeApe: "Code APE",
    },
    en: {
        statut: "Status",
        activite: "Activity",
        artisan: "Registered as a craftsperson",
        dateDebut: "Start of activity",
        caCetteAnnee: "Revenue this year",
        caAnDernier: "Revenue last year",
        enCouple: "Tax return",
        enfants: "Dependent children",
        autresRevenus: "Other household income, per year",
        objectifMensuel: "Target income, per month",
        fraisMensuels: "Expenses, per month",
        tarifJournalier: "Day rate",
        optionFiscale: "Income tax",
        acre: "ACRE",
        tvaFacturee: "VAT charged",
        civilite: "Title",
        prenoms: "First names",
        nom: "Last name",
        nomNaissance: "Birth name",
        dateNaissance: "Date of birth",
        villeNaissance: "City of birth",
        paysNaissance: "Country of birth",
        nationalite: "Nationality",
        voie: "Address",
        complement: "Address line 2",
        codePostal: "Postcode",
        commune: "City",
        email: "Email",
        telephone: "Phone",
        siret: "SIRET",
        codeApe: "APE code",
    },
};

export const LIBELLES_VALEURS = {
    fr: {
        statut: {
            projet: "Projet, rien n'est déclaré",
            micro: "Micro-entreprise",
            reel: "Entreprise individuelle au réel",
            eurl: "EURL à l'impôt sur les sociétés",
            eurlIr: "EURL à l'impôt sur le revenu",
            sasu: "SASU",
        },
        activite: {
            vente: "Vente de marchandises",
            services: "Prestations commerciales ou artisanales",
            bnc: "Activité libérale",
            cipav: "Profession libérale réglementée",
        },
        artisan: { true: "Oui", false: "Non" },
        enCouple: { true: "En couple", false: "Seul" },
        optionFiscale: { bareme: "Au barème", vfl: "Versement libératoire" },
        acre: { true: "Obtenue", false: "Non" },
        tvaFacturee: { true: "Oui, déjà", false: "Pas encore" },
        civilite: { madame: "Madame", monsieur: "Monsieur" },
    },
    en: {
        statut: {
            projet: "Project, nothing registered yet",
            micro: "Micro-enterprise",
            reel: "Sole trader on actual expenses",
            eurl: "EURL paying corporation tax",
            eurlIr: "EURL taxed as a sole trader",
            sasu: "SASU",
        },
        activite: {
            vente: "Selling goods",
            services: "Commercial or craft services",
            bnc: "Liberal profession",
            cipav: "Regulated liberal profession",
        },
        artisan: { true: "Yes", false: "No" },
        enCouple: { true: "As a couple", false: "Single" },
        optionFiscale: { bareme: "Standard scale", vfl: "Flat-rate withholding" },
        acre: { true: "Granted", false: "No" },
        tvaFacturee: { true: "Yes, already", false: "Not yet" },
        civilite: { madame: "Ms", monsieur: "Mr" },
    },
};

const MONTANTS = new Set([
    "caCetteAnnee",
    "caAnDernier",
    "autresRevenus",
    "objectifMensuel",
    "fraisMensuels",
    "tarifJournalier",
]);
const DATES = new Set(["dateDebut", "dateNaissance"]);

export function euros(valeur, lang = "fr") {
    return new Intl.NumberFormat(lang === "en" ? "en-GB" : "fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(Math.round(valeur));
}

/** Une date ISO écrite en toutes lettres, lue à midi pour qu'aucun fuseau ne la décale. */
export function dateLongue(iso, lang = "fr") {
    if (!iso) return "";
    const [a, m, j] = String(iso).slice(0, 10).split("-").map(Number);
    const d = new Date(a, m - 1, j, 12);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
    }).format(d);
}

/** La valeur d'un champ, telle qu'on l'écrit au lecteur. */
export function valeurLisible(champ, valeur, lang = "fr") {
    if (valeur === null || valeur === undefined || valeur === "") return "";
    if (MONTANTS.has(champ)) return euros(valeur, lang);
    if (DATES.has(champ)) return dateLongue(valeur, lang);
    const table = LIBELLES_VALEURS[lang]?.[champ] || LIBELLES_VALEURS.fr[champ];
    if (table) return table[String(valeur)] ?? String(valeur);
    return String(valeur);
}

export function libelleChamp(champ, lang = "fr") {
    return (LIBELLES_CHAMPS[lang] || LIBELLES_CHAMPS.fr)[champ] || champ;
}

// ---------------------------------------------------------------------------
// Les échéances de la page « Votre situation »
// ---------------------------------------------------------------------------

/** Les échéances que la page ajoute à celles des outils. */
const ECHEANCES_EN_PLUS = {
    fr: {
        "cfe-solde": "Solde de la CFE",
        "impot-acomptes": "Acompte d'impôt ouvert sur impots.gouv.fr",
        "vfl-option-annuelle": "Dernier jour pour demander le versement libératoire pour l'an prochain",
        "vfl-renoncer": "Dernier jour pour renoncer au versement libératoire pour l'an prochain",
    },
    en: {
        "cfe-solde": "CFE balance",
        "impot-acomptes": "Tax instalment opened on impots.gouv.fr",
        "vfl-option-annuelle": "Last day to opt for the flat-rate income tax payment for next year",
        "vfl-renoncer": "Last day to give up the flat-rate income tax payment for next year",
    },
};

export function libelleEcheance(cle, famille, lang = "fr") {
    const l = lang === "en" ? "en" : "fr";
    return (
        ECHEANCES_EN_PLUS[l][cle] ||
        (famille === "societe" ? LIBELLES_ECHEANCES.societe[l].ponctuelles[cle] : LIBELLES_ECHEANCES.micro[l][cle]) ||
        cle
    );
}
